import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import moment from "moment";
import axios, { CancelToken } from "axios";

import { GetShotGunTemplate, PutShotGunTemplateApply, IApplyShotgunTemplate } from "api/rpc/2024-04/facilityAdmin/teesheet/shotgun";
import { GetLeague } from "api/rpc/2024-04/facilityAdmin/league/league";
import { GetTournament } from "api/rpc/2024-04/facilityAdmin/tournament/tournament";
import { StatusCode } from "api/protocols";

import { showError, showSuccess } from "redux/actions/ui";

import { convertTime } from "helpers/Helpers";

import Sheet from "components/sheet/Sheet";
import FormLayout from "components/form/FormLayout";
import { Select } from "components/select/index";
import Checkbox from "components/form/checkbox/Checkbox";
import TimePick from "components/timePick/TimePick";
import Spin from "components/spin/spin";

import { ITeeTimeNew, IBookingCategory } from "redux/reducers/models/teetime";
import { ITournament } from "redux/reducers/models/tournament";
import { ILeague } from "redux/reducers/models/league";
import { ICourse, IDivision } from "redux/reducers/models/facility";
import { TShotgunTemplate } from "redux/reducers/models/teesheet";

import "./createShotgunModal.scss";

function isNamedDivision(division: IDivision, side: "front" | "back") {
  const location = division.location?.toLowerCase();
  const title = division.title?.trim().toLowerCase();

  return location === side || title === side;
}

function minutesToTimeString(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
}

function timeStringToMinutes(timeString: string): number {
  const [hours, minutes] = timeString.split(":");

  return +hours * 60 + +minutes;
}

interface ICreateShotgunModal {
  open: boolean;
  teetime: ITeeTimeNew;
  courses: ICourse[];
  bookingCategories: IBookingCategory[];
  templates: TShotgunTemplate[];
  onOk: () => void;
  onCancel: () => void;
}

interface IShotGunTemplateState {
  shotgun_template_id: number;
  tee_sheet_id: number;
  start_time: string;
  duration: string;
  lead_time: string;
  full_round_lead_time: string;
  nine_hole_lead_time: string;
  division_id: number;
  turn_division_id: number;
  tournament_id: number;
  league_id: number;
  tournaments: ITournament[];
  leagues: Array<ILeague>;
  tournamentQuery: string;
  leagueQuery: string;
  booking_category_ids: Array<number>;
}

export default function CreateShotgunModal(props: ICreateShotgunModal) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { teeSheetStore } = useAppSelector(store => store);

  const { open, teetime, courses, bookingCategories, templates, onOk, onCancel } = props;

  const [nineHoleSide, setNineHoleSide] = useState<"front" | "back" | null>(null);
  const [isReverseShotgun, setIsReverseShotgun] = useState(false);
  const [openLeadAsNineHoles, setOpenLeadAsNineHoles] = useState(false);
  const prefilledCourseId = useRef<number | null>(null);

  const [state, setState] = useState<IShotGunTemplateState>({
    shotgun_template_id: undefined,
    tee_sheet_id: teetime?.slots?.[0]?.tee_sheet_id,
    start_time: teetime?.start_time,
    duration: "",
    lead_time: "",
    full_round_lead_time: "",
    nine_hole_lead_time: "",
    division_id: undefined,
    turn_division_id: undefined,
    tournament_id: undefined,
    league_id: undefined,
    tournaments: [],
    leagues: [],
    tournamentQuery: "",
    leagueQuery: "",
    booking_category_ids: [],
  });

  const teetimeCourse = useMemo(() => {
    if (!teetime || !courses) {
      return undefined;
    }

    return courses.find(course => course.id === teetime.slots?.[0]?.course_id);
  }, [teetime, courses]);

  const divisions: IDivision[] = useMemo(() => teetimeCourse?.divisions ?? [], [teetimeCourse]);

  useEffect(() => {
    if (!open) {
      prefilledCourseId.current = null;
      setNineHoleSide(null);
      setIsReverseShotgun(false);
      setOpenLeadAsNineHoles(false);
      return;
    }

    if (!teetimeCourse || teetimeCourse.holes !== 18 || prefilledCourseId.current === teetimeCourse.id) {
      return;
    }

    const courseDivisions = teetimeCourse.divisions ?? [];
    const frontDivision = courseDivisions.find(division => isNamedDivision(division, "front"));
    const backDivision = courseDivisions.find(division => isNamedDivision(division, "back"));
    if (!frontDivision && !backDivision) {
      return;
    }

    prefilledCourseId.current = teetimeCourse.id;
    updateShotgunTemplateState({
      ...(frontDivision ? { division_id: frontDivision.id } : {}),
      ...(backDivision ? { turn_division_id: backDivision.id } : {}),
    });
  }, [open, teetimeCourse]);

  const divisionsDisabled = state.shotgun_template_id === undefined;

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadShotgunTournaments(source.token);
    return () => source.cancel();
  }, [state.tournamentQuery]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadShotgunLeagues(source.token);
    return () => source.cancel();
  }, [state.leagueQuery]);

  function updateShotgunTemplateState(newShotgunTemplateState: Partial<IShotGunTemplateState>) {
    setState((cur: IShotGunTemplateState) => {
      return { ...cur, ...newShotgunTemplateState };
    });
  }

  async function loadShotgunTournaments(token?: CancelToken) {
    const date = teeSheetStore?.selectedDate as Date;

    const tournamentRes = await GetTournament(
      {
        search: state.tournamentQuery,
        year: !state.tournamentQuery ? date?.getFullYear() ?? new Date()?.getFullYear() : undefined,
      },
      false,
      token,
    );

    if (token && token.reason) {
      return;
    }
    if (tournamentRes.status !== StatusCode.OK) {
      return;
    }

    updateShotgunTemplateState({ tournaments: tournamentRes.data });
  }

  async function loadShotgunLeagues(token?: CancelToken) {
    const date = teeSheetStore?.selectedDate as Date;

    const leagueRes = await GetLeague(
      {
        search: state.leagueQuery,
        year: !state.tournamentQuery ? date?.getFullYear() ?? new Date()?.getFullYear() : undefined,
      },
      false,
      token,
    );

    if (token && token.reason) {
      return;
    }
    if (leagueRes.status !== StatusCode.OK) {
      return;
    }
    updateShotgunTemplateState({ leagues: leagueRes.data });
  }

  function parseShotgunHoleNumber(startHole: string): number {
    const match = startHole.match(/^(\d+)/);
    return match ? Number(match[1]) : 0;
  }

  async function loadSelectedTemplateMetadata(templateId: number) {
    const templateRes = await GetShotGunTemplate({ id: templateId, extended: true }, false);
    const teeTimes: Array<{ start_hole: string; position?: number }> =
      templateRes.status === StatusCode.OK ? templateRes.data?.[0]?.tee_times ?? [] : [];
    const orderedTeeTimes = [...teeTimes].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    const holes = orderedTeeTimes
      .map(teeTime => parseShotgunHoleNumber(teeTime.start_hole))
      .filter(hole => hole > 0);
    if (holes.length === 0) {
      setNineHoleSide(null);
      setIsReverseShotgun(false);
      setOpenLeadAsNineHoles(false);
      return;
    }
    const templateIsReverse = holes.length > 1 && holes[0] > holes[holes.length - 1];
    if (templateIsReverse) {
      setIsReverseShotgun(true);
      setNineHoleSide(null);
      const courseRoundMinutes = (teetimeCourse?.front_nine_time ?? 0) + (teetimeCourse?.back_nine_time ?? 0);
      updateShotgunTemplateState({
        lead_time: "",
        ...(courseRoundMinutes > 0
          ? { full_round_lead_time: minutesToTimeString(courseRoundMinutes) }
          : {}),
      });
      return;
    }
    setIsReverseShotgun(false);
    setOpenLeadAsNineHoles(false);
    updateShotgunTemplateState({
      full_round_lead_time: "",
      nine_hole_lead_time: "",
    });
    const minHole = holes.reduce((min, hole) => Math.min(min, hole), holes[0]);
    const maxHole = holes.reduce((max, hole) => Math.max(max, hole), holes[0]);
    if (maxHole <= 9) {
      setNineHoleSide("front");
      updateShotgunTemplateState({ turn_division_id: undefined });
    } else if (minHole >= 10) {
      setNineHoleSide("back");
      updateShotgunTemplateState({ division_id: undefined });
    } else {
      setNineHoleSide(null);
    }
  }

  async function applyShotgunTemplate() {
    const durationMins = timeStringToMinutes(state.duration);

    const params: IApplyShotgunTemplate = {
      tee_sheet_id: state.tee_sheet_id,
      start_time: state.start_time,
      duration: durationMins,
      lead_time: isReverseShotgun ? 0 : timeStringToMinutes(state.lead_time),
      shotgun_template_id: state.shotgun_template_id,
      tournament_id: state.tournament_id,
      league_id: state.league_id,
      booking_category_ids: state.booking_category_ids,
    };

    if (nineHoleSide === "front") {
      params.division_id = state.division_id;
    } else if (nineHoleSide === "back") {
      params.turn_division_id = state.turn_division_id;
    } else {
      params.division_id = state.division_id;
      params.turn_division_id = state.turn_division_id;
    }

    if (isReverseShotgun) {
      params.open_lead_as_nine_holes = openLeadAsNineHoles;
      params.full_round_lead_time = timeStringToMinutes(state.full_round_lead_time);
      params.nine_hole_lead_time = timeStringToMinutes(state.nine_hole_lead_time);
    }

    const applyShotgunTemplateRes = await PutShotGunTemplateApply(params, true);
    if (applyShotgunTemplateRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error applying shotgun template")));
      return;
    }

    dispatch(showSuccess(t("Successfully applied shotgun template")));

    updateShotgunTemplateState({
      tee_sheet_id: null,
      start_time: "",
      duration: "",
      lead_time: "",
      full_round_lead_time: "",
      nine_hole_lead_time: "",
      shotgun_template_id: null,
      division_id: null,
      turn_division_id: null,
      tournament_id: undefined,
      league_id: undefined,
    });

    void onOk();
  }

  function handleShotgunDropdownChange(value: number, property: string) {
    if ((property === "tournament_id" || property === "league_id") && value === -1) {
      value = undefined;
    }
    updateShotgunTemplateState({ [property]: value });
  }

  function handleShotgunTimePicker(value: string, property: string) {
    updateShotgunTemplateState({ [property]: value });
  }

  function handleBookingCategoryChange(event: any) {
    const value = parseInt(event?.target?.value as string);
    if (isNaN(value)) {
      return;
    }

    if (state.booking_category_ids.find(bookingCategoryId => bookingCategoryId === value) === undefined) {
      setState(prevState => ({
        ...prevState,
        booking_category_ids: [...prevState.booking_category_ids, value],
      }));
    } else {
      const updatedBookingCategoryIds = state.booking_category_ids.filter(
        bookingCategoryId => bookingCategoryId !== value,
      );
      setState(prevState => ({ ...prevState, booking_category_ids: updatedBookingCategoryIds }));
    }
  }

  return (
    <Sheet
      open={open}
      size="medium"
      height="flexible"
      closable
      title={`${t("Tee Time -")} ${teetime ? convertTime(teetime?.start_time) : null}`}
      supportText={t("Create Shotgun")}
      onCancel={() => onCancel()}
      onOk={() => applyShotgunTemplate()}
      cancelText={t("Cancel")}
      okText={t("Apply Template")}
      okDisabled={
        (nineHoleSide !== "back" && state.division_id === undefined) ||
        (nineHoleSide !== "front" && state.turn_division_id === undefined) ||
        !state.duration ||
        state.duration === "" ||
        (isReverseShotgun
          ? !state.full_round_lead_time ||
            state.full_round_lead_time === "" ||
            !state.nine_hole_lead_time ||
            state.nine_hole_lead_time === "" ||
            timeStringToMinutes(state.full_round_lead_time) <= timeStringToMinutes(state.nine_hole_lead_time)
          : !state.lead_time || state.lead_time === "") ||
        state.shotgun_template_id === undefined ||
        state.start_time === "" ||
        state.tee_sheet_id === null
      }
    >
      <div className="create-shotgun-modal">
        <section className="create-shotgun-modal__section">
          <h3 className="create-shotgun-modal__section-title">{t("Template")}</h3>
          <p className="create-shotgun-modal__section-description">
            {t("Choose the shotgun layout and which booking categories can use these tee times.")}
          </p>
          <FormLayout>
            <FormLayout.Group>
              <Select
                defaultValue={state.shotgun_template_id}
                onChange={(value: number) => {
                  updateShotgunTemplateState({ shotgun_template_id: value });
                  void loadSelectedTemplateMetadata(value);
                }}
                label={t("Template")}
              >
                {templates?.map((template, index: number) => {
                  return (
                    <Select.Option key={index} value={template.id} name={template.title}>
                      <span>{template.title}</span>
                    </Select.Option>
                  );
                })}
              </Select>
              <div className="create-shotgun-modal__booking-categories">
                <div className="rc-select-dropdown-label create-shotgun-modal__booking-categories-label">
                  <label>{t("Booking Categories")}</label>
                </div>
                {bookingCategories ? (
                  <div>
                    {bookingCategories.map((bookingCategory, index: number) => {
                      return (
                        <Checkbox
                          key={index}
                          size="small"
                          onChange={handleBookingCategoryChange}
                          value={bookingCategory.id}
                          checked={
                            state.booking_category_ids.find(
                              bookingCategoryId => bookingCategoryId === bookingCategory.id,
                            ) !== undefined
                          }
                          label={bookingCategory.title}
                        />
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ height: "32px" }}>
                    <Spin />
                  </div>
                )}
              </div>
            </FormLayout.Group>
          </FormLayout>
        </section>

        {divisions && (
          <section className="create-shotgun-modal__section">
            <h3 className="create-shotgun-modal__section-title">{t("Schedule")}</h3>
            <p className="create-shotgun-modal__section-description">
              {t("Set when the shotgun starts and which divisions groups will play.")}
            </p>
            <FormLayout>
              <FormLayout.Group>
                <TimePick
                  value={state.start_time}
                  onChange={timeString => updateShotgunTemplateState({ start_time: timeString })}
                  label={t("Start Time")}
                  size="large"
                />
                {nineHoleSide !== "back" && (
                  <Select
                    key={`front-division-${state.shotgun_template_id ?? "none"}-${state.division_id ?? "none"}`}
                    defaultValue={state.division_id}
                    disabled={divisionsDisabled}
                    onChange={(value: number) => handleShotgunDropdownChange(value, "division_id")}
                    label={t("Front Division")}
                  >
                    {divisions?.map((division: any, index: number) => {
                      return (
                        <Select.Option key={index} value={division.id} name={division.title}>
                          <span>{division.title}</span>
                        </Select.Option>
                      );
                    })}
                  </Select>
                )}

                {nineHoleSide !== "front" && (
                  <Select
                    key={`back-division-${state.shotgun_template_id ?? "none"}-${state.turn_division_id ?? "none"}`}
                    defaultValue={state.turn_division_id}
                    disabled={divisionsDisabled}
                    onChange={(value: number) => handleShotgunDropdownChange(value, "turn_division_id")}
                    label={t("Back Division")}
                  >
                    {divisions?.map((division: any, index: number) => {
                      return (
                        <Select.Option key={index} value={division.id} name={division.title}>
                          <span>{division.title}</span>
                        </Select.Option>
                      );
                    })}
                  </Select>
                )}
              </FormLayout.Group>
            </FormLayout>
          </section>
        )}

        <section className="create-shotgun-modal__section">
          <h3 className="create-shotgun-modal__section-title">{t("Event")}</h3>
          <p className="create-shotgun-modal__section-description">
            {t("Optionally link this shotgun to a tournament or league.")}
          </p>
          <FormLayout>
            <FormLayout.Group>
              <Select
                showSearch
                showDropDownOnFocus={true}
                placeholder={t("Search Tournaments...")}
                onSearch={(query: string) => setState(prevState => ({ ...prevState, tournamentQuery: query }))}
                onChange={(value: number) => handleShotgunDropdownChange(value, "tournament_id")}
                label={t("Assign Tournament")}
                disabled={state.league_id !== undefined}
              >
                <Select.Option value={-1} key={-1} name={t("None")}>
                  <div>{t("None")}</div>
                </Select.Option>

                {state.tournaments
                  ?.filter(tournament => !moment(tournament.date).isBefore(moment(), "day"))
                  .map((tournament: any, index: number) => {
                    return (
                      <Select.Option key={index} value={tournament.id} name={tournament.name}>
                        <div className="flex items-center">
                          <span>{tournament.name}</span>
                          <span className="shotgun-tournament-date">
                            {moment(tournament.date as string).format("LL")}
                          </span>
                        </div>
                      </Select.Option>
                    );
                  })}

                <Select.Option disabled value={-2} key={-2} name={""}>
                  <div>
                    <hr style={{ margin: "0px" }} />
                  </div>
                </Select.Option>

                {state.tournaments
                  ?.filter(tournament => moment(tournament.date).isBefore(moment(), "day"))
                  .map((tournament: any, index: number) => {
                    return (
                      <Select.Option key={index} value={tournament.id} name={tournament.name}>
                        <div className="flex items-center">
                          <span>{tournament.name}</span>
                          <span className="shotgun-tournament-date">
                            {moment(tournament.date as string).format("LL")}
                          </span>
                        </div>
                      </Select.Option>
                    );
                  })}
              </Select>
              <Select
                showSearch
                showDropDownOnFocus={true}
                placeholder={t("Search leagues...")}
                onSearch={(query: string) => setState(prevState => ({ ...prevState, leagueQuery: query }))}
                onChange={(value: number) => handleShotgunDropdownChange(value, "league_id")}
                label={t("Assign League")}
                disabled={state.tournament_id !== undefined}
              >
                <Select.Option value={-1} key={-1} name={"None"}>
                  <div>{t("None")}</div>
                </Select.Option>
                {state.leagues?.map((league: any, index: number) => {
                  return (
                    <Select.Option key={index} value={league.id} name={league.name}>
                      <span>{league.name}</span>
                    </Select.Option>
                  );
                })}
              </Select>
            </FormLayout.Group>
          </FormLayout>
        </section>

        {isReverseShotgun ? (
          <>
            <section className="create-shotgun-modal__section">
              <h3 className="create-shotgun-modal__section-title">{t("Before Start")}</h3>
              <p className="create-shotgun-modal__section-description">
                {t(
                  "Reverse shotguns block tee times before the start so groups can play through hole 1. Set how long each lead window should last.",
                )}
              </p>
              <FormLayout>
                <FormLayout.Group alignStart>
                  <TimePick
                    value={state.full_round_lead_time}
                    onChange={(timeString: string) => handleShotgunTimePicker(timeString, "full_round_lead_time")}
                    label={t("Full Round Lead Time")}
                    hideMeridiem={12}
                    helpText={t(
                      "How far before the shotgun start to block tee times for a full round. Example: 4:30 keeps 7:30 AM blocked for a 12:00 PM start.",
                    )}
                  />
                  <TimePick
                    value={state.nine_hole_lead_time}
                    onChange={(timeString: string) => handleShotgunTimePicker(timeString, "nine_hole_lead_time")}
                    label={t("Nine Hole Lead Time")}
                    hideMeridiem={12}
                    helpText={t(
                      "How far before the shotgun start the shorter lead window begins. Tee times in this window can stay open for 9-hole bookings. Example: 2:15 opens 9:45 AM for a 12:00 PM start.",
                    )}
                  />
                </FormLayout.Group>
              </FormLayout>
            </section>

            <section className="create-shotgun-modal__section">
              <h3 className="create-shotgun-modal__section-title">{t("After Start")}</h3>
              <p className="create-shotgun-modal__section-description">
                {t("Set how long tee times stay closed after the shotgun begins.")}
              </p>
              <FormLayout>
                <FormLayout.Group alignStart>
                  <TimePick
                    value={state.duration}
                    onChange={(timeString: string) => handleShotgunTimePicker(timeString, "duration")}
                    label={t("Turn Time")}
                    hideMeridiem={12}
                    helpText={t(
                      "How long after the shotgun start tee times stay closed while the closest group plays down through hole 1.",
                    )}
                  />
                </FormLayout.Group>
              </FormLayout>
            </section>

            <section className="create-shotgun-modal__section">
              <h3 className="create-shotgun-modal__section-title">{t("Nine-Hole Bookings")}</h3>
              <p className="create-shotgun-modal__section-description">
                {t("Optionally allow 9-hole bookings in the shorter lead window before the shotgun starts.")}
              </p>
              <FormLayout>
                <FormLayout.Group alignStart>
                  <Checkbox
                    size="medium"
                    checked={openLeadAsNineHoles}
                    onChange={event => setOpenLeadAsNineHoles(event.target.checked)}
                    label={t("Open tee times in the nine hole lead window for 9 holes only")}
                    supportText={t(
                      "When checked, hole 1 tee times between the nine hole lead time and the shotgun start stay open and are limited to 9-hole bookings.",
                    )}
                  />
                </FormLayout.Group>
              </FormLayout>
            </section>
          </>
        ) : (
          <section className="create-shotgun-modal__section">
            <h3 className="create-shotgun-modal__section-title">{t("Timing")}</h3>
            <p className="create-shotgun-modal__section-description">
              {t("Set how long the shotgun lasts and how far in advance tee times should be blocked.")}
            </p>
            <FormLayout>
              <FormLayout.Group alignStart>
                <TimePick
                  value={state.duration}
                  onChange={(timeString: string) => handleShotgunTimePicker(timeString, "duration")}
                  label={t("Duration")}
                  hideMeridiem={12}
                  helpText={t("How long the shotgun blocks tee times after the start time.")}
                />
                <TimePick
                  value={state.lead_time}
                  onChange={(timeString: string) => handleShotgunTimePicker(timeString, "lead_time")}
                  label={t("Lead Time")}
                  hideMeridiem={12}
                  helpText={t("How far before the shotgun start tee times should be blocked.")}
                />
              </FormLayout.Group>
            </FormLayout>
          </section>
        )}
      </div>
    </Sheet>
  );
}
